import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import {
  CaretLeft, UserPlus, Trophy, Trash, Barbell, Medal, UsersThree, Crown, ListChecks,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const WINDOWS = [
  { id: "7d", label: "7 DAYS" },
  { id: "30d", label: "30 DAYS" },
  { id: "all", label: "ALL TIME" },
];

const rankBadge = (rank) => {
  if (rank === 1) return "bg-brand-accent text-surface-base";
  if (rank === 2) return "bg-white/80 text-surface-base";
  if (rank === 3) return "bg-amber-700/80 text-white";
  return "bg-white/5 text-text-secondary";
};

const avatar = (m) => {
  if (m.picture) {
    const src = m.picture.startsWith("/api/") ? `${BACKEND_URL}${m.picture}` : m.picture;
    return <img src={src} alt="" className="w-9 h-9 rounded-full object-cover" />;
  }
  return (
    <div className="w-9 h-9 rounded-full bg-brand-primary flex items-center justify-center text-xs font-bold">
      {(m.name || "?")[0].toUpperCase()}
    </div>
  );
};

export default function CoachTeamDetail() {
  const { team_id } = useParams();
  const [team, setTeam] = useState(null);
  const [board, setBoard] = useState(null);
  const [win, setWin] = useState("7d");
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState("");
  const [adding, setAdding] = useState(false);
  const [programs, setPrograms] = useState([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignProgramId, setAssignProgramId] = useState("");
  const [assignDate, setAssignDate] = useState(new Date().toISOString().slice(0, 10));
  const [assigningTeam, setAssigningTeam] = useState(false);

  const loadTeam = useCallback(() => {
    setLoading(true);
    api.get(`/coach/teams/${team_id}`)
      .then(({ data }) => setTeam(data))
      .catch(() => toast.error("Failed to load team"))
      .finally(() => setLoading(false));
  }, [team_id]);

  const loadBoard = useCallback(() => {
    api.get(`/coach/teams/${team_id}/leaderboard?window=${win}`)
      .then(({ data }) => setBoard(data))
      .catch(() => {});
  }, [team_id, win]);

  useEffect(() => { loadTeam(); }, [loadTeam]);
  useEffect(() => { loadBoard(); }, [loadBoard]);

  useEffect(() => {
    api.get("/coach/clients").then(({ data }) => setClients(data)).catch(() => {});
    api.get("/coach/programs").then(({ data }) => setPrograms(data)).catch(() => {});
  }, []);

  const availableClients = clients.filter(
    (c) => !(team?.members || []).some((m) => m.user_id === c.client?.user_id)
  );

  const addMember = async () => {
    if (!selectedClient) { toast.error("Choose an athlete"); return; }
    setAdding(true);
    try {
      await api.post(`/coach/teams/${team_id}/members`, { client_id: selectedClient });
      toast.success("Member added");
      setSelectedClient(""); setAddOpen(false);
      loadTeam(); loadBoard();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add member");
    } finally {
      setAdding(false);
    }
  };

  const removeMember = async (uid, name) => {
    if (!window.confirm(`Remove ${name} from team?`)) return;
    try {
      await api.delete(`/coach/teams/${team_id}/members/${uid}`);
      toast.success("Removed");
      loadTeam(); loadBoard();
    } catch {
      toast.error("Failed to remove");
    }
  };

  const assignToTeam = async () => {
    if (!assignProgramId) { toast.error("Choose a program"); return; }
    setAssigningTeam(true);
    try {
      const { data } = await api.post(`/coach/teams/${team_id}/assign`, {
        program_id: assignProgramId,
        start_date: assignDate,
      });
      if (data.assigned_count > 0 && data.skipped_count > 0) {
        toast.success(`Assigned to ${data.assigned_count}. Skipped ${data.skipped_count} (already had it).`);
      } else if (data.assigned_count > 0) {
        toast.success(`Assigned to ${data.assigned_count} member${data.assigned_count === 1 ? "" : "s"}.`);
      } else {
        toast(`Every member already has this program.`);
      }
      setAssignOpen(false);
      setAssignProgramId("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to assign");
    } finally {
      setAssigningTeam(false);
    }
  };

  if (loading && !team) {
    return <div className="p-10 text-text-secondary label-tiny" data-testid="team-detail-loading">Loading team...</div>;
  }
  if (!team) {
    return <div className="p-10 text-text-secondary" data-testid="team-detail-missing">Team not found.</div>;
  }

  return (
    <div className="max-w-6xl mx-auto p-6 lg:p-10 space-y-8" data-testid="coach-team-detail-page">
      <Link
        to="/coach/teams"
        className="inline-flex items-center gap-1 text-sm text-text-secondary hover:text-white"
        data-testid="team-back-link"
      >
        <CaretLeft size={14} /> BACK TO TEAMS
      </Link>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="label-tiny text-brand-accent tracking-widest mb-2">// TEAM</div>
          <h1 className="font-heading text-4xl sm:text-5xl" data-testid="team-name-heading">{team.name}</h1>
          {team.description && (
            <p className="text-text-secondary mt-2 max-w-2xl text-sm">{team.description}</p>
          )}
          <div className="flex items-center gap-4 mt-3 text-sm text-text-secondary">
            <span className="inline-flex items-center gap-1.5"><UsersThree size={14} /> {team.member_count} members</span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
            <DialogTrigger asChild>
              <button
                className="inline-flex items-center gap-2 border border-brand-accent text-brand-accent px-4 py-2.5 text-sm label-tiny tracking-wider hover:bg-brand-accent/10 transition"
                data-testid="team-assign-program-btn"
                disabled={team.member_count === 0}
                title={team.member_count === 0 ? "Add members first" : "Assign a program to the whole team"}
              >
                <ListChecks size={16} weight="bold" /> ASSIGN PROGRAM
              </button>
            </DialogTrigger>
            <DialogContent data-testid="team-assign-program-dialog">
              <DialogHeader>
                <DialogTitle>Assign a program to the team</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                {programs.length === 0 ? (
                  <div className="text-sm text-text-secondary" data-testid="team-assign-no-programs">
                    You have no programs yet. Create one in <span className="text-brand-accent">Coach → Programs</span> first.
                  </div>
                ) : (
                  <>
                    <label className="text-xs label-tiny text-text-secondary">PROGRAM</label>
                    <select
                      value={assignProgramId}
                      onChange={(e) => setAssignProgramId(e.target.value)}
                      className="w-full bg-white/[0.03] border border-white/10 px-3 py-2 text-sm text-white"
                      data-testid="team-assign-program-select"
                    >
                      <option value="">— Select a program —</option>
                      {programs.map((p) => (
                        <option key={p.program_id} value={p.program_id}>
                          {p.name} ({p.weeks || "?"}w · {p.sessions_per_week || "?"}x/wk)
                        </option>
                      ))}
                    </select>
                    <label className="text-xs label-tiny text-text-secondary">START DATE</label>
                    <Input
                      type="date"
                      value={assignDate}
                      onChange={(e) => setAssignDate(e.target.value)}
                      data-testid="team-assign-program-date"
                    />
                    <div className="text-xs text-text-secondary">
                      Will be assigned to all {team.member_count} active member{team.member_count === 1 ? "" : "s"}. Members who already have this program active will be skipped.
                    </div>
                    <button
                      onClick={assignToTeam}
                      disabled={assigningTeam || !assignProgramId}
                      className="w-full bg-brand-accent text-surface-base py-2.5 text-sm label-tiny tracking-wider disabled:opacity-60"
                      data-testid="team-assign-program-submit"
                    >
                      {assigningTeam ? "ASSIGNING..." : "ASSIGN TO TEAM"}
                    </button>
                  </>
                )}
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={addOpen} onOpenChange={setAddOpen}>
            <DialogTrigger asChild>
              <button
                className="inline-flex items-center gap-2 bg-brand-accent text-surface-base px-4 py-2.5 text-sm label-tiny tracking-wider hover:brightness-110 transition"
                data-testid="team-add-member-btn"
              >
                <UserPlus size={16} weight="bold" /> ADD MEMBER
              </button>
            </DialogTrigger>
            <DialogContent data-testid="team-add-member-dialog">
              <DialogHeader>
                <DialogTitle>Add athlete to team</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                {availableClients.length === 0 ? (
                  <div className="text-sm text-text-secondary" data-testid="team-add-no-clients">
                    All your active clients are already on this team, or you have no active clients yet.
                  </div>
                ) : (
                  <>
                    <label className="text-xs label-tiny text-text-secondary">ATHLETE</label>
                    <select
                      value={selectedClient}
                      onChange={(e) => setSelectedClient(e.target.value)}
                      className="w-full bg-white/[0.03] border border-white/10 px-3 py-2 text-sm text-white"
                      data-testid="team-add-member-select"
                    >
                      <option value="">— Select an active client —</option>
                      {availableClients.map((c) => (
                        <option key={c.client?.user_id} value={c.client?.user_id}>
                          {c.client?.name} ({c.client?.email})
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={addMember}
                      disabled={adding || !selectedClient}
                      className="w-full bg-brand-accent text-surface-base py-2.5 text-sm label-tiny tracking-wider disabled:opacity-60"
                      data-testid="team-add-member-submit"
                    >
                      {adding ? "ADDING..." : "ADD TO TEAM"}
                    </button>
                  </>
                )}
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Leaderboard */}
      <div className="border border-white/10 bg-white/[0.02]" data-testid="team-leaderboard">
        <div className="flex items-center justify-between gap-3 flex-wrap p-5 border-b border-white/10">
          <div className="flex items-center gap-2">
            <Trophy size={20} className="text-brand-accent" weight="duotone" />
            <div className="font-heading text-xl">Leaderboard</div>
          </div>
          <div className="flex items-center gap-1 border border-white/10 p-1" data-testid="team-leaderboard-window-toggle">
            {WINDOWS.map((w) => (
              <button
                key={w.id}
                onClick={() => setWin(w.id)}
                data-testid={`team-window-${w.id}`}
                className={`px-3 py-1.5 text-xs label-tiny tracking-wider transition ${
                  win === w.id ? "bg-brand-accent text-surface-base" : "text-text-secondary hover:text-white"
                }`}
              >
                {w.label}
              </button>
            ))}
          </div>
        </div>

        {(!board || board.entries.length === 0) ? (
          <div className="p-10 text-center text-text-secondary" data-testid="team-leaderboard-empty">
            <Medal size={40} className="mx-auto text-brand-accent mb-2" weight="duotone" />
            <div className="text-sm">
              {!board ? "Loading rankings..." :
                team.member_count === 0
                  ? "Add athletes to see the leaderboard come alive."
                  : "No workouts or PRs logged in this window yet."}
            </div>
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {board.entries.map((e) => (
              <div
                key={e.user_id}
                className="flex items-center gap-4 p-4 hover:bg-white/[0.02]"
                data-testid={`leaderboard-row-${e.user_id}`}
              >
                <div className={`w-9 h-9 flex items-center justify-center text-sm font-bold ${rankBadge(e.rank)}`}>
                  {e.rank === 1 ? <Crown size={16} weight="fill" /> : `#${e.rank}`}
                </div>
                {avatar(e)}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate" data-testid={`leaderboard-name-${e.user_id}`}>{e.name}</div>
                  <div className="text-xs text-text-secondary flex gap-3">
                    <span><Barbell size={11} className="inline mr-0.5" /> {e.workouts} workouts</span>
                    <span><Trophy size={11} className="inline mr-0.5" /> {e.prs} PRs</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xl font-heading text-brand-accent tabular-nums" data-testid={`leaderboard-points-${e.user_id}`}>{e.points}</div>
                  <div className="text-[10px] label-tiny text-text-secondary">PTS</div>
                </div>
                <button
                  onClick={() => removeMember(e.user_id, e.name)}
                  className="text-text-secondary hover:text-red-400 opacity-40 hover:opacity-100 transition"
                  data-testid={`leaderboard-remove-${e.user_id}`}
                  aria-label={`Remove ${e.name}`}
                >
                  <Trash size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="p-3 border-t border-white/5 text-[10px] label-tiny text-text-secondary/70 tracking-wider">
          POINTS = WORKOUTS × 10 + NEW PRs × 25
        </div>
      </div>
    </div>
  );
}
